/**
 * Utility functions for handling API errors
 */

/**
 * FastAPI/Pydantic validation error format
 */
interface FastAPIValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/**
 * API error response structure
 */
interface APIErrorResponse {
  response?: {
    data?: {
      detail?: string | FastAPIValidationError[] | string[];
      message?: string;
    };
  };
  message?: string;
}

/**
 * Technical error prefixes that should not be shown to users
 */
const TECHNICAL_ERROR_PREFIXES = [
  'TypeError',
  'ReferenceError',
  'SyntaxError',
  'RangeError',
  'URIError',
  'EvalError',
];

/**
 * Maximum length for error messages to prevent UI overflow
 */
const MAX_ERROR_LENGTH = 500;

/**
 * Truncate a string to a maximum length with ellipsis
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + '...';
}

/**
 * Check if an error is a technical JavaScript error
 */
function isTechnicalError(err: unknown): boolean {
  // Check if it's a native JavaScript Error with a technical type
  if (err && typeof err === 'object' && 'name' in err) {
    const errorName = (err as { name: unknown }).name;
    if (typeof errorName === 'string') {
      return TECHNICAL_ERROR_PREFIXES.includes(errorName);
    }
  }

  // Fallback: check if the message starts with a technical error prefix
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message: unknown }).message;
    if (typeof message === 'string') {
      return TECHNICAL_ERROR_PREFIXES.some(prefix => message.startsWith(prefix));
    }
  }

  return false;
}

/**
 * Extract a user-friendly error message from an API error response
 * Handles both FastAPI validation errors and generic error responses
 *
 * @param err - The error object from axios/fetch
 * @param defaultMessage - Default message if no specific error details found
 * @returns A formatted error message string
 */
export function extractErrorMessage(
  err: unknown,
  defaultMessage: string = 'An error occurred'
): string {
  const apiErr = err as APIErrorResponse;

  // Check for API error detail
  if (apiErr?.response?.data?.detail !== undefined) {
    const detail = apiErr.response.data.detail;

    // Handle string detail
    if (typeof detail === 'string') {
      return truncate(detail, MAX_ERROR_LENGTH);
    }

    // Handle array of errors
    if (Array.isArray(detail)) {
      const messages = detail
        .map((error: string | FastAPIValidationError) => {
          // Handle string errors (custom format)
          if (typeof error === 'string') {
            return error;
          }
          // Handle Pydantic validation error objects (standard format)
          if (typeof error === 'object' && error !== null) {
            return error.msg || 'Validation error';
          }
          return 'Validation error';
        })
        .filter((msg): msg is string => Boolean(msg));

      return truncate(messages.join(', '), MAX_ERROR_LENGTH);
    }

    // Handle other types (convert to string)
    return truncate(String(detail), MAX_ERROR_LENGTH);
  }

  // Check for alternative message format
  if (apiErr?.response?.data?.message) {
    return truncate(apiErr.response.data.message, MAX_ERROR_LENGTH);
  }

  // Check for Error.message, but filter out technical errors
  if (apiErr?.message && typeof apiErr.message === 'string') {
    if (!isTechnicalError(apiErr)) {
      return truncate(apiErr.message, MAX_ERROR_LENGTH);
    }
  }

  return defaultMessage;
}
